import { Button, Typography, useMutationWithToast } from '@pesto/ui';
import { RotateCcw } from 'lucide-react';

import type { ModalProps, ProgramModalPayload } from '../../types/modal.types';

import { useRestoreProgramTwisp } from '../../../__generated__/rest/api';

export const RestoreProgramTwispComponent = ({ payload, api }: ModalProps<ProgramModalPayload, true>) => {
  const { programId, programName } = payload;
  const { handleMutation } = useMutationWithToast();
  const restoreProgramTwisp = useRestoreProgramTwisp();

  const handleRestore = () => {
    void handleMutation({
      mutation: () => restoreProgramTwisp.mutateAsync({ programId }),
      extractSuccess: (result) => !!result?.resources,
      debugLabel: 'restore-program-twisp',
      toast: {
        loading: 'Restoring Twisp resources...',
        successToast: (result) => {
          const statuses = Object.values(result.resources).map((resource) => resource.status);

          if (statuses.every((status) => status === 'created')) {
            return { title: 'Journal, exposure, and ISO created' };
          }

          if (statuses.every((status) => status === 'already_present')) {
            return { title: 'Journal, exposure, and ISO already present' };
          }

          return { title: 'Missing Twisp resources created; existing resources kept' };
        },
        error: 'Failed to restore Twisp resources',
      },
      onSuccess: () => api.close(true),
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="bg-primary/10 flex h-12 w-12 items-center justify-center rounded-full">
          <RotateCcw className="text-primary h-6 w-6" />
        </div>
        <div>
          <Typography variant="headerSmall" className="font-semibold">
            Restore Twisp resources
          </Typography>
          <Typography variant="bodySmall" className="text-muted-foreground">
            {programName}
          </Typography>
        </div>
      </div>

      <div className="space-y-3">
        <Typography variant="body">
          This restores the program journal, exposure account set, and ISO configuration from their stored IDs.
        </Typography>

        <div className="border-warning/20 bg-warning/10 rounded-lg border p-4">
          <Typography variant="bodySmall" className="text-warning">
            It does not restore loan accounts, balances, or transaction history.
          </Typography>
        </div>
      </div>

      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => api.cancel()} disabled={restoreProgramTwisp.isPending}>
          Cancel
        </Button>
        <Button
          variant="primary"
          aria-label="Restore Twisp resources"
          onClick={handleRestore}
          disabled={restoreProgramTwisp.isPending}
          loading={restoreProgramTwisp.isPending}
        >
          Restore Twisp resources
        </Button>
      </div>
    </div>
  );
};
